import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, ShieldCheck, RefreshCw, Unlink, X, ShieldAlert } from 'lucide-react';
import { Button } from '../../components/UI/Button';
import paymentService, { type MerchantStatus } from '../../services/paymentService';
import { userService } from '../../services/userService';
import { useToast } from '../../components/UI/Toast';

interface PaymentSettingsProps {
  onSwitchToVerification?: () => void;
}

const PaymentSettings: React.FC<PaymentSettingsProps> = ({ onSwitchToVerification }) => {
  const { showToast, ToastWrapper } = useToast();
  const [status, setStatus] = useState<MerchantStatus | null>(null);
  const [verified, setVerified] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alipayAccount, setAlipayAccount] = useState('');
  const [holderName, setHolderName] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  // Replace-account modal state
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [newAccount, setNewAccount] = useState('');
  const [newHolder, setNewHolder] = useState('');
  const [replacing, setReplacing] = useState(false);

  // Deregister modal state
  const [showDeregisterModal, setShowDeregisterModal] = useState(false);
  const [deregistering, setDeregistering] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [statusRes, profileRes] = await Promise.all([
        paymentService.getMerchantStatus(),
        userService.getUserProfile().catch(() => null),
      ]);
      if (statusRes.success) {
        const data = (statusRes.data as any)?.data || statusRes.data;
        setStatus(data);
      }
      const profile = profileRes?.data?.userProfile;
      setVerified(Boolean(profile?.verified));
    } catch {
      setStatus({ registered: false });
      setVerified(false);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!alipayAccount.trim() || !holderName.trim()) {
      showToast('请填写完整', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const res = await paymentService.setupMerchant(alipayAccount.trim(), holderName.trim(), contactPhone.trim() || undefined);
      if (res.success) {
        showToast('支付设置完成', 'success');
        await load();
      } else {
        showToast((res as any).error || '注册失败', 'error');
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || '注册失败', 'error');
    }
    setSubmitting(false);
  };

  const handleReplace = async () => {
    if (!newAccount.trim() || !newHolder.trim()) {
      showToast('请填写完整', 'error');
      return;
    }
    setReplacing(true);
    try {
      const res = await paymentService.replaceAccount(newAccount.trim(), newHolder.trim());
      if (res.success) {
        showToast('账户已更换', 'success');
        setShowReplaceModal(false);
        setNewAccount('');
        setNewHolder('');
        await load();
      } else {
        showToast((res as any).error || '更换失败', 'error');
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || '更换失败', 'error');
    }
    setReplacing(false);
  };

  const handleDeregister = async () => {
    setDeregistering(true);
    try {
      const res = await paymentService.deregisterMerchant();
      if (res.success) {
        showToast('已解除商户绑定', 'success');
        setShowDeregisterModal(false);
        await load();
      } else {
        const errMsg = (res as any).error || '解除绑定失败';
        const blocking = (res as any).blockingContracts as string[] | undefined;
        showToast(blocking?.length ? `${errMsg}（${blocking.length} 个合同在进行中）` : errMsg, 'error');
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || '解除绑定失败', 'error');
    }
    setDeregistering(false);
  };

  if (loading) {
    return <div className="p-6 text-slate-500">加载中...</div>;
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <ToastWrapper />
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">支付设置</h1>
        <p className="text-sm text-slate-500 mt-1">
          绑定您的支付宝账户，客户在合同款项支付时将转入此账户。
        </p>
      </div>

      {verified === false ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-6 space-y-4">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <h2 className="text-base font-semibold text-amber-900">请先完成实人认证</h2>
              <p className="text-sm text-amber-900/80">
                绑定支付宝账户前需要完成实人认证。我们会校验您填写的支付宝账户姓名与实人认证一致，确保款项安全。
              </p>
            </div>
          </div>
          {onSwitchToVerification && (
            <div className="pl-8">
              <Button onClick={onSwitchToVerification} className="inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                前往实人认证
              </Button>
            </div>
          )}
        </div>
      ) : status?.registered ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-5 space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <h2 className="text-base font-semibold text-emerald-900">已完成支付设置</h2>
          </div>
          <dl className="text-sm grid grid-cols-1 gap-2">
            <div className="flex">
              <dt className="w-32 text-slate-500">支付宝账号</dt>
              <dd className="text-slate-900 font-mono">{status.alipayAccount}</dd>
            </div>
            <div className="flex">
              <dt className="w-32 text-slate-500">实名持有人</dt>
              <dd className="text-slate-900">{status.holderName}</dd>
            </div>
            {status.subMerchantId && (
              <div className="flex">
                <dt className="w-32 text-slate-500">商户号</dt>
                <dd className="text-slate-900 font-mono text-xs">{status.subMerchantId}</dd>
              </div>
            )}
            {status.registeredAt && (
              <div className="flex">
                <dt className="w-32 text-slate-500">绑定时间</dt>
                <dd className="text-slate-900">{new Date(Number(status.registeredAt)).toLocaleString('zh-CN')}</dd>
              </div>
            )}
          </dl>
          <div className="flex gap-2 pt-3 border-t border-emerald-200">
            <Button variant="outline" onClick={() => setShowReplaceModal(true)} className="inline-flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              更换账户
            </Button>
            <Button variant="outline" onClick={() => setShowDeregisterModal(true)} className="inline-flex items-center gap-2 text-red-600 hover:bg-red-50 border-red-200">
              <Unlink className="w-4 h-4" />
              解除绑定
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="rounded-lg border border-slate-200 bg-white p-6 space-y-5">
          <div className="flex items-start gap-3 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-amber-900">
              支付宝账户的实名姓名必须与您在平台完成的实人认证姓名一致，否则注册会被拒绝。
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              支付宝账号 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={alipayAccount}
              onChange={(e) => setAlipayAccount(e.target.value)}
              placeholder="手机号 / 邮箱"
              className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              实名持有人 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={holderName}
              onChange={(e) => setHolderName(e.target.value)}
              placeholder="必须与支付宝实名一致"
              className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">联系电话</label>
            <input
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="选填"
              className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
            />
          </div>

          <Button type="submit" disabled={submitting} className="inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            {submitting ? '注册中...' : '完成支付设置'}
          </Button>
        </form>
      )}

      {verified !== false && (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-5 text-sm space-y-2">
          <h3 className="font-medium text-slate-900">说明</h3>
          <ul className="list-disc pl-5 space-y-1 text-slate-600">
            <li>支付宝账户的实名姓名必须与您在平台的实人认证姓名一致。</li>
            <li>每个合同里程碑通过审核后，对应款项会发送至此账户。</li>
            <li>更换账户后，进行中的合同仍可正常收款；解除绑定要求您没有任何待支付或进行中的合同。</li>
          </ul>
        </div>
      )}

      {/* Replace account modal */}
      {showReplaceModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-900">更换支付宝账户</h3>
              <button onClick={() => setShowReplaceModal(false)} className="p-1 rounded hover:bg-slate-100">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-600">
                商户号保持不变，进行中的合同仍可正常收款。新账户的实名姓名必须与您的实人认证姓名一致。
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  新支付宝账号 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newAccount}
                  onChange={(e) => setNewAccount(e.target.value)}
                  placeholder="手机号 / 邮箱"
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  实名持有人 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newHolder}
                  onChange={(e) => setNewHolder(e.target.value)}
                  placeholder="必须与支付宝实名一致"
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setShowReplaceModal(false)} disabled={replacing}>取消</Button>
                <Button onClick={handleReplace} disabled={replacing}>
                  {replacing ? '更换中...' : '确认更换'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deregister modal */}
      {showDeregisterModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-900">解除商户绑定</h3>
              <button onClick={() => setShowDeregisterModal(false)} className="p-1 rounded hover:bg-slate-100">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3 rounded-md bg-red-50 border border-red-200 p-3 text-sm">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                <div className="text-red-900 space-y-1">
                  <p className="font-medium">此操作将清除您的支付宝商户绑定</p>
                  <p>解除后您将无法创建新合同，需要重新完成支付宝商户注册才能再次接单。</p>
                </div>
              </div>
              <p className="text-sm text-slate-600">
                如有进行中的合同（待支付或已托管），系统会拒绝此操作并列出阻塞的合同。
              </p>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setShowDeregisterModal(false)} disabled={deregistering}>取消</Button>
                <Button
                  onClick={handleDeregister}
                  disabled={deregistering}
                  className="bg-red-600 hover:bg-red-700 text-white border-red-600"
                >
                  {deregistering ? '处理中...' : '确认解除'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSettings;
